/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    AlertsAction,
    AlertsActionType,
    alertsErrorAction,
    fetchAlertsPageSuccessAction,
    fetchNewAlertsSuccessAction,
    setBaseDateToPollNewAlerts,
    updateAlertsSuccess,
} from '../actions/alertsActions';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { from, interval, merge, of } from 'rxjs';
import { alertsClient } from '../api/alerts/alertsClient';
import { RootState } from '../store.typings';
import { AlertsSearchParams } from '../api/alerts/alertsClient.typings';
import { AlertsState } from '../reducers/alertsReducer';
import { AxiosError } from 'axios';
import { parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';

type AlertsEpic = Epic<AlertsAction, AlertsAction, RootState>;

export const fetchAlertsEpic: AlertsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(
            AlertsActionType.OPEN_ALERTS_PAGE,
            AlertsActionType.CHANGE_ALERTS_PAGE,
            AlertsActionType.CHANGE_FILTERS,
            AlertsActionType.CHANGE_SORT,
            AlertsActionType.SHOW_NEW_ALERTS,
        ),
        switchMap(() => {
            const state = state$.value.alertsReducer;
            const searchParams = getSearchParamsFromState(state);

            return from(alertsClient.getAlertsList(searchParams)).pipe(
                map((list) => fetchAlertsPageSuccessAction(list)),

                catchError((err: AxiosError) =>
                    of(alertsErrorAction(parseError(err, i18n.t('AlertsScreens.errors.failedLoadAlertsList')))),
                ),

                takeUntil(action$.ofType(AlertsActionType.EXIT_ALERTS_PAGE)),
            );
        }),
    );
};

const POLLING_INTERVAL = 10000;

export const fetchNewAlertsByIntervalEpic: AlertsEpic = (action$, state$) => {
    const setBaseIdToPollNewAlerts$ = action$.pipe(
        ofType(AlertsActionType.OPEN_ALERTS_PAGE, AlertsActionType.CHANGE_FILTERS),
        switchMap(() => {
            const searchParams = getSearchParamsFromState(state$.value.alertsReducer);
            searchParams.offset = 0;
            searchParams.limit = 1;
            searchParams.sort = 'inserted,desc';

            return from(alertsClient.getAlertsList(searchParams)).pipe(
                map((alertsList) => {
                    return setBaseDateToPollNewAlerts({
                        dateFrom: alertsList.count ? alertsList.alerts[0].inserted : null,
                        alertId: alertsList.count ? alertsList.alerts[0].id : null,
                    });
                }),

                catchError((err: AxiosError) =>
                    of(alertsErrorAction(parseError(err, i18n.t('AlertsScreens.errors.failedLoadAlertsList')))),
                ),
            );
        }),
    );

    const fetchNewAlerts$ = action$.pipe(
        ofType(AlertsActionType.SET_BASE_TIME_TO_POLL_NEW_ALERTS),
        switchMap(() => {
            return interval(POLLING_INTERVAL).pipe(
                switchMap(() => {
                    const searchParams = getSearchParamsFromState(state$.value.alertsReducer);
                    searchParams.offset = 0;
                    searchParams.limit = 1;
                    searchParams.sort = 'inserted,asc';

                    const dateFrom = state$.value.alertsReducer.newAlertsInfo.dateFrom;

                    if (dateFrom) {
                        searchParams.dateFrom = dateFrom;
                    }
                    return alertsClient.getAlertsList(searchParams);
                }),

                map((list) => fetchNewAlertsSuccessAction(list)),

                catchError((err: AxiosError) =>
                    of(alertsErrorAction(parseError(err, i18n.t('AlertsScreens.errors.failedLoadAlertsList')))),
                ),

                takeUntil(action$.ofType(AlertsActionType.EXIT_ALERTS_PAGE)),
            );
        }),
    );

    return merge(setBaseIdToPollNewAlerts$, fetchNewAlerts$);
};

export const updateAlertsEpic: AlertsEpic = (action$) => {
    return action$.pipe(
        ofType(AlertsActionType.UPDATE_ALERTS),
        switchMap((action) => {
            return from(alertsClient.updateAlerts(action.payload)).pipe(
                map((updatedAlerts) => updateAlertsSuccess(updatedAlerts)),

                catchError((err: AxiosError) =>
                    of(alertsErrorAction(parseError(err, i18n.t('errors.failedTryAgain')))),
                ),
            );
        }),
    );
};

function getSearchParamsFromState(state: AlertsState): AlertsSearchParams {
    return {
        ...state.filterParams,
        sort: state.sort,
        offset: state.page,
        limit: state.pageSize,
    };
}
